import { gql } from "@apollo/client";
import EditIcon from "@mui/icons-material/Edit";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton, Switch, TableCell, TableRow, TextField } from "@mui/material";
import { useState } from "react";
import { useCreateServiceMutation, useUpdateServiceMutation } from "~/graphql/__generated__/graphql";
import { Service } from "~/providers/OrgConfigProvider";

export type ServiceDraft = Pick<Service, "id" | "name" | "description" | "duration" | "active">;

export const emptyService: ServiceDraft = { id: 0, name: "", description: "", duration: 30, active: true };

const truncatedCell = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const;

interface Props {
  service: Service
}

const ServiceRow = ({ service }: Props) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [update, { loading }] = useUpdateServiceMutation();

  const toggleActive = () => update({
    variables: { ...toVariables(service), active: !service.active },
    optimisticResponse: { updateService: { ...service, __typename: "ServiceSelectItem", active: !service.active } },
  });

  return <TableRow>
    <TableCell sx={truncatedCell}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <span>{service.name}</span>
        <IconButton
          size="small"
          onClick={() => setDialogOpen(true)}
          sx={{ minWidth: 'auto', padding: '4px' }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </div>
    </TableCell>
    <TableCell>{service.duration} min</TableCell>
    <TableCell sx={truncatedCell}>{service.description || '—'}</TableCell>
    <TableCell>
      <Switch checked={service.active} disabled={loading} onChange={toggleActive} size="small" />
    </TableCell>

    {dialogOpen && <ServiceDialog onClose={() => setDialogOpen(false)} service={service} />}
  </TableRow>
}

export default ServiceRow;

const toVariables = ({ id, name, description, duration, active }: ServiceDraft) =>
  ({ id, name, description, duration, active });

interface ServiceDialogProps {
  onClose: () => void;
  service: ServiceDraft;
}

export const ServiceDialog = ({
  onClose,
  service,
}: ServiceDialogProps) => {
  const [local, setLocal] = useState(service);
  const [create, { loading: createLoading }] = useCreateServiceMutation();
  const [update, { loading: updateLoading }] = useUpdateServiceMutation();

  const loading = createLoading || updateLoading;
  const isNew = !local.id;

  const handleSave = async () => {
    const { id, ...fields } = toVariables(local);

    if (isNew) {
      await create({
        variables: fields,
        awaitRefetchQueries: true,
        refetchQueries: ["OrgConfig"],
      });
    } else {
      await update({ variables: { id, ...fields } });
    }

    onClose();
  };

  return (
    <Dialog open={true} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isNew ? 'Add Service' : 'Edit Service'}
      </DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Name"
          fullWidth
          variant="outlined"
          value={local.name}
          onChange={(e) => setLocal({ ...local, name: e.target.value })}
          disabled={loading}
          sx={{ mb: 2 }}
        />
        <TextField
          margin="dense"
          label="Duration (minutes)"
          type="number"
          fullWidth
          variant="outlined"
          value={local.duration}
          onChange={(e) => Number(e.target.value) >= 0 && setLocal({ ...local, duration: Number(e.target.value) })}
          disabled={loading}
          sx={{ mb: 2 }}
        />
        <TextField
          margin="dense"
          label="Description"
          fullWidth
          multiline
          minRows={2}
          variant="outlined"
          value={local.description || ""}
          onChange={(e) => setLocal({ ...local, description: e.target.value })}
          disabled={loading}
          sx={{ mb: 2 }}
        />
        <FormControlLabel
          control={
            <Switch
              checked={local.active}
              onChange={(e) => setLocal({ ...local, active: e.target.checked })}
              disabled={loading}
            />
          }
          label="Active"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          loading={loading}
          disabled={!local.name?.trim() || !local.duration}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

gql`
  mutation CreateService($name: String!, $description: String, $duration: Int!, $active: Boolean) @permission(requires: manageOrgConfig) {
    createService(name: $name, description: $description, duration: $duration, active: $active) {
      id
    }
  }
`;

gql`
  mutation UpdateService($id: Int!, $name: String, $description: String, $duration: Int, $active: Boolean) @permission(requires: manageOrgConfig) {
    updateService(id: $id, name: $name, description: $description, duration: $duration, active: $active) {
      id
      name
      description
      duration
      active
    }
  }
`;
